import styled from '@emotion/styled';
import momentTz from 'moment-timezone';
import { DatePicker as AntDatePicker } from 'antd';

import { FORTA_BLACK, FORTA_WHITE, FORTA_ERROR, FORTA_BLUE, FORTA_GRAY2 } from '../../utils/colors';
import 'antd/dist/antd.min.css';
import { Themes, DEFAULT_INPUT_HEIGHT } from '../../constants';
import { getThemeColor } from '../../utils/common';
import CalendarOutlinedIcon from '../svgs/CalendarOutlinedIcon';

import { Box, Typography } from '@mui/material';

import FHTooltip from './FHTooltip';

const StyledAntdDateTimePicker = styled(AntDatePicker, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'error' &&
    prop !== 'inputHeight' &&
    prop !== 'hideTimezoneDisplay' &&
    prop !== 'timezoneSx',
})<any>(({ colorTheme, coloredBackground, inputHeight, error, hideTimezoneDisplay, sx }) => ({
  cursor: 'pointer',
  borderRadius: hideTimezoneDisplay ? '10px' : '10px 0 0 10px',
  height: inputHeight,
  backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
  border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
  borderRight: hideTimezoneDisplay
    ? (sx?.border ?? `1px solid ${FORTA_BLUE.light1}`)
    : 'none !important',
  '&:focus-within': {
    border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
    borderRight: hideTimezoneDisplay ? 'inherit' : 'none !important',
  },

  '&:hover': {
    border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
    borderRight: hideTimezoneDisplay ? 'inherit' : 'none !important',
  },
  '& .ant-picker-suffix': {
    color: getThemeColor(colorTheme).primary,
  },

  '& .ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner': {
    backgroundColor: FORTA_BLUE.primary,
    color: FORTA_WHITE,
  },
  '&.ant-picker-disabled': {
    backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
    border: `1px solid ${FORTA_BLUE.light1}`,
    '& .ant-picker-input>input[disabled]': {
      color: FORTA_BLACK,
    },
    '& .ant-picker-suffix': {
      color: FORTA_WHITE,
    },
  },
  ...sx,
  borderTopRightRadius: hideTimezoneDisplay ? (sx?.borderRadius ?? 0) : 0,
  borderBottomRightRadius: hideTimezoneDisplay ? (sx?.borderRadius ?? 0) : 0,
}));

const FHAntdDateTimePicker = (props: any) => {
  const {
    colorTheme = Themes.Primary,
    coloredBackground = false,
    inputHeight = DEFAULT_INPUT_HEIGHT,
    error = false,
    disabledIconColor,
    iconWidth = 18,
    iconHeight = 18,
    disabled,
    hideTimezoneDisplay,
    tooltipBackground,
    sx,
    timezoneSx = {},
    showCalendarIcon = false,
  } = props;

  const userTimezone = momentTz.tz.guess(true);
  const nowInUserTimezone = momentTz().tz(userTimezone);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <StyledAntdDateTimePicker
        {...props}
        error={error}
        inputHeight={inputHeight}
        colorTheme={colorTheme}
        suffixIcon={
          showCalendarIcon ? (
            <CalendarOutlinedIcon
              color={
                disabled
                  ? disabledIconColor
                    ? disabledIconColor
                    : getThemeColor(colorTheme).light2
                  : getThemeColor(colorTheme).primary
              }
              width={iconWidth}
              height={iconHeight}
            />
          ) : null
        }
        renderExtraFooter={() => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              sx={{
                height: 40,
                cursor: 'pointer',
                p: 1,
                borderLeft: 'none',
              }}
            >
              {`${userTimezone} (${nowInUserTimezone.format('z')})`}
            </Typography>
          </Box>
        )}
        coloredBackground={coloredBackground}
      />
      {!hideTimezoneDisplay && (
        <FHTooltip
          arrow
          title={`Your current timezone is ${userTimezone} (${nowInUserTimezone.format('z')})`}
          sx={{
            borderRadius: '8px',
            display: 'flex',
            padding: '10px',
            maxWidth: '370px',
          }}
          tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
          tooltipbackground={tooltipBackground ? tooltipBackground : FORTA_WHITE}
        >
          <Typography
            sx={{
              height: 40,
              cursor: 'pointer',
              border: error
                ? `2px solid ${FORTA_ERROR}`
                : sx?.border
                  ? sx?.border
                  : `1px solid ${FORTA_BLUE.light1}`,
              p: 1,
              borderLeft: 'none',
              borderRadius: '0 10px 10px 0',
              backgroundColor: FORTA_GRAY2,
              ...timezoneSx,
            }}
          >
            {nowInUserTimezone.format('z')}
          </Typography>
        </FHTooltip>
      )}
    </Box>
  );
};

export default FHAntdDateTimePicker;
